import { useState } from "react";
import { MessageCircle, Bot, UserRound, ShoppingBag, BarChart3, Zap, CheckCheck, Smartphone } from "lucide-react";
import { API_BASE, api, errMsg } from "@/lib/api";

const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === "true";
const AUTH_ERRORS = {
  invalid_state: "El acceso caducó o ya se utilizó. Vuelve a entrar con Google.",
  cancelled: "Se canceló el acceso con Google.",
  not_allowed: "Esta cuenta no tiene acceso al negocio. Consulta al propietario.",
};

const FEATURES = [
  { icon: Bot, t: "Bot con IA entrenado", d: "Responde con tu catálogo, horarios y preguntas frecuentes, 24/7." },
  { icon: ShoppingBag, t: "Toma pedidos y reservas", d: "El bot registra cada pedido confirmado automáticamente." },
  { icon: UserRound, t: "Pase a un asesor", d: "Si el cliente pide un humano, el bot se detiene y te avisa." },
  { icon: Zap, t: "Respuestas rápidas y etiquetas", d: "Atajos con / y clasificación de clientes." },
  { icon: BarChart3, t: "Estadísticas", d: "Mensajes, ventas y cuánto resuelve el bot." },
  { icon: Smartphone, t: "Prueba tu asistente", d: "Comprueba sus respuestas antes de conectar tu número." },
];

const DEMO = [
  { me: true, t: "Hola! tienen pastel de chocolate?" },
  { me: false, t: "¡Hola! Sí, la porción cuesta 4,50 USD. ¿Te la llevo con un capuchino? ☕" },
  { me: true, t: "Sí, 2 de cada uno a Calle 5 #20" },
  { me: false, t: "¡Listo! Pedido registrado: total 16 USD. Llega en 30-45 min." },
];

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.37-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.83z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.52 6.16-4.52z" />
    </svg>
  );
}

export default function SignIn() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    const code = new URLSearchParams(window.location.search).get("auth_error");
    return code ? (AUTH_ERRORS[code] || "No se pudo completar el acceso con Google. Inténtalo de nuevo.") : "";
  });

  const handleDemoLogin = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/demo");
      window.location.assign("/dashboard");
    } catch (e) {
      setError(errMsg(e, "No se pudo abrir la demo local. Comprueba que el servidor esté iniciado."));
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/auth/config");
      if (!data.google_ready) {
        setError("El administrador está terminando de conectar Google a esta instalación. Vuelve a intentarlo cuando la configuración esté completa.");
        setLoading(false);
        return;
      }
      window.location.assign(`${API_BASE}/auth/google/start`);
    } catch {
      setError("No se pudo conectar con el servidor. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5]" data-testid="signin-page">
      <div className="h-56 bg-[#00A884] absolute inset-x-0 top-0" />
      <div className="relative max-w-6xl mx-auto px-6 pt-8 pb-16">
        <div className="flex items-center gap-2 text-white">
          <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center"><MessageCircle className="h-5 w-5" fill="white" /></div>
          <span className="font-display font-extrabold text-lg">Respondly</span>
        </div>

        <div className="mt-10 bg-white rounded-3xl shadow-xl grid lg:grid-cols-[1.15fr_1fr] overflow-hidden">
          <div className="p-8 sm:p-12 animate-rise">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">Chatbot de WhatsApp para negocios</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-black text-[#111B21] leading-[1.05]">
              Tu negocio responde, vende y agenda en WhatsApp. Solo.
            </h1>
            <p className="mt-5 text-base text-slate-600 max-w-lg">
              Un asistente con IA que conoce tus productos, toma pedidos y reservas, y pasa la conversación a tu equipo cuando hace falta.
            </p>
            <button data-testid={DEMO_MODE ? "sign-in-demo-button" : "sign-in-google-button"}
              onClick={DEMO_MODE ? handleDemoLogin : handleLogin} disabled={loading}
              className="mt-8 inline-flex items-center gap-3 h-12 px-6 rounded-full border border-slate-300 bg-white hover:bg-slate-50 hover:shadow-md font-semibold text-[#111B21] transition-[box-shadow,background-color] disabled:opacity-60 disabled:cursor-wait">
              {DEMO_MODE ? <Smartphone className="h-5 w-5" /> : <GoogleIcon />}
              {DEMO_MODE ? (loading ? "Abriendo demo…" : "Entrar a la demo local") : (loading ? "Conectando…" : "Entrar con Google")}
            </button>
            {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
            <p className="mt-3 text-xs text-slate-500">
              {DEMO_MODE ? "Demostración local: respuestas de prueba, sin envíos a WhatsApp." : "Acceso para el equipo del negocio. Sin contraseñas."}
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-x-6 gap-y-5">
              {FEATURES.map(({ icon: Icon, t, d }, i) => (
                <div key={t} className="flex gap-3 animate-rise" style={{ animationDelay: `${120 + i * 60}ms` }}>
                  <div className="h-9 w-9 shrink-0 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Icon className="h-[18px] w-[18px]" /></div>
                  <div><p className="text-sm font-bold text-[#111B21]">{t}</p><p className="text-xs text-slate-500 mt-0.5">{d}</p></div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#F7F8FA] p-8 flex items-center justify-center border-l border-slate-100">
            <div className="w-[300px] rounded-[2.5rem] border-[10px] border-[#111B21] bg-[#111B21] shadow-2xl overflow-hidden">
              <div className="bg-[#008069] text-white px-4 pt-6 pb-3 flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-emerald-200 text-emerald-800 font-bold text-sm flex items-center justify-center">C</div>
                <div><p className="text-sm font-semibold">Café Aurora</p><p className="text-[10px] text-emerald-100">en línea</p></div>
              </div>
              <div className="wa-wallpaper h-[380px] p-3 space-y-2">
                {DEMO.map((m, i) => (
                  <div key={i} className={`flex ${m.me ? "justify-end" : "justify-start"} animate-msg`} style={{ animationDelay: `${400 + i * 500}ms` }}>
                    <div className={`max-w-[85%] text-[12.5px] px-2.5 py-1.5 rounded-lg shadow-sm ${m.me ? "bg-[#D9FDD3] bubble-out" : "bg-white bubble-in"}`}>
                      {!m.me && <p className="text-[10px] font-bold text-emerald-700 flex items-center gap-1"><Bot className="h-2.5 w-2.5" />Bot IA</p>}
                      {m.t}
                      <span className="float-right ml-2 mt-1 text-[9px] text-slate-500 flex items-center">12:0{i}{m.me && <CheckCheck className="h-3 w-3 text-sky-500 ml-0.5" />}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
